import * as React from "react";

// ---------------------------------------------------------------------------
// useListReorder — drag a row grip to reorder a vertical list
// ---------------------------------------------------------------------------
//
// The EntitySelector counterpart of data-table's useColumnReorder, sharing its
// house mechanics: one delegated pointerdown listener on the rows container
// (grips mark themselves `data-esgrip`; each row already carries
// `data-entity-id`), a 5px movement threshold before a drag arms, and an rAF
// loop that drives edge auto-scroll plus the drop indicator while dragging.
//
// The drop target is the nearest row boundary, measured from the live row
// rects each frame (rows are natural-height, so there is no width-map
// equivalent to prefix-sum over). The indicator y is container-relative so the
// consumer can absolutely position a line inside the rows container. On drop
// the synthesized click the pointerup dispatches is swallowed in the capture
// phase — otherwise releasing a drag over a row would also toggle it.

const DRAG_THRESHOLD_PX = 5;
const EDGE_SCROLL_ZONE_PX = 32;
const EDGE_SCROLL_SPEED_PX = 10;

type PendingDrag = {
  rowId: string;
  startX: number;
  startY: number;
};

export function useListReorder({
  rowsRef,
  enabled,
  onDrop,
}: {
  /** The container whose direct/deep children are the `[data-entity-id]` rows. */
  rowsRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  /** beforeId = row the dragged row lands before; null = end of the list. */
  onDrop: (draggedId: string, beforeId: string | null) => void;
}): {
  draggingId: string | null;
  /** Container-relative y of the drop indicator line, null while not dragging. */
  indicatorY: number | null;
} {
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;
  const onDropRef = React.useRef(onDrop);
  onDropRef.current = onDrop;

  const pointerRef = React.useRef({ x: 0, y: 0 });
  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const [indicatorY, setIndicatorY] = React.useState<number | null>(null);

  React.useEffect(() => {
    const el = rowsRef.current;
    if (!enabled || !el) return;

    let pending: PendingDrag | null = null;
    let active = false;
    let raf: number | null = null;
    // Index into the row list: boundary b sits at the top edge of row b
    // (b = row count → after the last row).
    let lastBoundaryIndex: number | null = null;
    let prevCursor = "";
    let prevUserSelect = "";

    const rowEls = (): HTMLElement[] =>
      Array.from(el.querySelectorAll<HTMLElement>("[data-entity-id]"));

    const scroller = (): HTMLElement | null =>
      el.closest<HTMLElement>("[data-slot=scroll-area-viewport]");

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const pointerY = pointerRef.current.y;

      const viewport = scroller();
      if (viewport) {
        const vRect = viewport.getBoundingClientRect();
        if (pointerY < vRect.top + EDGE_SCROLL_ZONE_PX) {
          viewport.scrollTop -= EDGE_SCROLL_SPEED_PX;
        } else if (pointerY > vRect.bottom - EDGE_SCROLL_ZONE_PX) {
          viewport.scrollTop += EDGE_SCROLL_SPEED_PX;
        }
      }

      const rows = rowEls();
      // Backstop against state drift: rows are re-read every frame, so a
      // re-render that empties the list mid-drag leaves nothing to index.
      if (!rows.length) return;
      const containerTop = el.getBoundingClientRect().top;
      let best = 0;
      let bestY = 0;
      let bestDist = Infinity;
      for (let b = 0; b <= rows.length; b++) {
        // Boundary y: top of row b, or the bottom of the final row.
        const rect =
          b < rows.length
            ? rows[b]!.getBoundingClientRect()
            : rows[rows.length - 1]!.getBoundingClientRect();
        const y = b < rows.length ? rect.top : rect.bottom;
        const dist = Math.abs(y - pointerY);
        if (dist < bestDist) {
          bestDist = dist;
          best = b;
          bestY = y - containerTop;
        }
      }
      if (best !== lastBoundaryIndex) {
        lastBoundaryIndex = best;
        setIndicatorY(bestY);
      }
    };

    const cleanupDrag = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
      if (active) {
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        setDraggingId(null);
        setIndicatorY(null);
      }
      pending = null;
      active = false;
      lastBoundaryIndex = null;
    };

    // Swallow the click the terminal pointerup synthesizes on whatever row
    // the pointer was released over — a completed drag must not also toggle
    // that row's selection.
    const swallowNextClick = () => {
      const swallow = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener("click", swallow, { capture: true, once: true });
      // The synthesized click fires in the same task as pointerup; anything
      // still armed afterwards would eat an unrelated press.
      setTimeout(() => {
        document.removeEventListener("click", swallow, { capture: true });
      }, 0);
    };

    const onMove = (e: PointerEvent) => {
      // `pending` is set by the pointerdown that attached this listener, and
      // the only thing that clears it is `cleanupDrag`, which detaches the
      // listener in the same statement block. A move here always has a drag.
      const drag = pending!;
      pointerRef.current = { x: e.clientX, y: e.clientY };
      if (!active) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD_PX) return;
        active = true;
        prevCursor = document.body.style.cursor;
        prevUserSelect = document.body.style.userSelect;
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        setDraggingId(drag.rowId);
        raf = requestAnimationFrame(tick);
      }
      e.preventDefault();
    };

    const onUp = () => {
      const wasActive = active;
      const draggedId = pending!.rowId;
      const boundary = lastBoundaryIndex;
      cleanupDrag();
      if (!wasActive) return;
      swallowNextClick();
      // Backstop against frame timing: the boundary is resolved by the rAF
      // loop the arming move schedules, and rAF callbacks run after the
      // frame's input is dispatched. A press that crosses the 5px threshold
      // and releases inside one frame lands here with no boundary yet —
      // drop it rather than guess an index.
      if (boundary === null) return;
      const rows = rowEls();
      const beforeId = rows[boundary]?.getAttribute("data-entity-id") ?? null;
      if (beforeId === draggedId) return;
      onDropRef.current(draggedId, beforeId);
    };

    const onDown = (e: PointerEvent) => {
      if (!enabledRef.current || e.button !== 0 || pending) return;
      // A dispatched pointer event on an element listener always carries an
      // Element target; the DOM type is wide because an event that was never
      // dispatched has none.
      const target = e.target as Element;
      if (!target.closest("[data-esgrip]")) return;
      const row = target.closest("[data-entity-id]");
      if (!row || !el.contains(row)) return;
      // `closest` matched the attribute selector, so the attribute is present.
      const rowId = row.getAttribute("data-entity-id")!;
      // The grip is not a click target, so nothing needs the default press.
      e.preventDefault();
      pending = { rowId, startX: e.clientX, startY: e.clientY };
      pointerRef.current = { x: e.clientX, y: e.clientY };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
    };

    el.addEventListener("pointerdown", onDown);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      cleanupDrag();
    };
  }, [enabled, rowsRef]);

  return { draggingId, indicatorY };
}
